import { Caption1, makeStyles, tokens } from "@fluentui/react-components";
import { useState } from "react";
import { AppDialog, DialogActionsBar, DialogCheckbox, DialogField } from "@/components/app-dialog";
import { FuiButton, FuiDropdown, FuiInput } from "@/components/fui-control";
import type { UserResource } from "@/engine/types";
import { recordWorkspaceAudit } from "@/features/access/workspace-access";
import { useCatalogStore } from "@/store/catalog-store";
import {
  DIRECTORY_ROLE_LABEL,
  findPersonByEmail,
  grantOf,
  inviteOutcome,
  invitedGrant,
  nextTeamId,
  type DirectoryKind,
  type DirectoryRole,
} from "./directory";
import { putDirectoryGrant, useDirectoryGrants } from "./directory-store";

const ROLES: Exclude<DirectoryRole, "owner">[] = ["admin", "editor", "viewer"];

const useStyles = makeStyles({
  line: {
    color: tokens.colorNeutralForeground3,
    whiteSpace: "normal",
  },
});

export function InviteMemberDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const styles = useStyles();
  const resources = useCatalogStore((s) => s.resources);
  const teams = useCatalogStore((s) => s.teams);
  const addResource = useCatalogStore((s) => s.addResource);
  const updateResource = useCatalogStore((s) => s.updateResource);
  const grants = useDirectoryGrants();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<Exclude<DirectoryRole, "owner">>("editor");
  const [guest, setGuest] = useState(false);
  const [teamId, setTeamId] = useState("");
  const [error, setError] = useState("");

  const teamOptions = [
    { value: "", label: "소속 없음" },
    ...Object.values(teams).map((team) => ({ value: team.id, label: team.name })),
  ];

  const submit = () => {
    const address = email.trim();
    if (!address.includes("@") || !address.includes(".")) {
      setError("이메일 주소를 입력하세요.");
      return;
    }
    const people = Object.values(resources).map((resource) => ({ id: resource.id, email: resource.email }));
    const existingId = findPersonByEmail(people, address);
    const outcome = inviteOutcome(existingId ? grantOf(grants, existingId) : null);
    if (outcome === "already") {
      setError("이미 이 워크스페이스에 있습니다.");
      return;
    }
    const displayName = name.trim() || (existingId ? resources[existingId]?.displayName : "") || address;
    if (!displayName) {
      setError("이름을 입력하세요.");
      return;
    }
    const kind: DirectoryKind = guest ? "guest" : "member";
    let resourceId = existingId;
    if (!resourceId) {
      resourceId = addResource(newResource(displayName, address, role, kind, teamId || undefined));
    } else {
      const moved = nextTeamId(resources[resourceId]?.teamId, teamId || undefined);
      if (moved !== null) updateResource(resourceId, { teamId: moved });
    }
    putDirectoryGrant(resourceId, invitedGrant(role, kind));
    recordWorkspaceAudit("멤버 초대", `${address}을 ${DIRECTORY_ROLE_LABEL[role]}로 초대했습니다.`);
    onClose();
  };

  return (
    <AppDialog
      open={open}
      onClose={onClose}
      width="md"
      title="멤버 초대"
      footer={<DialogActionsBar onCancel={onClose} onConfirm={submit} confirmLabel="초대" disabled={!email.trim()} />}
    >
      <DialogField label="이름">
        <FuiInput density="dialog" value={name} autoFocus onChange={(_, data) => setName(data.value)} placeholder="예: 김PM" />
      </DialogField>
      <DialogField label="이메일" required>
        <FuiInput density="dialog" value={email} onChange={(_, data) => { setEmail(data.value); setError(""); }} placeholder="name@company.com" />
      </DialogField>
      <DialogField label="역할">
        <FuiDropdown
          density="dialog"
          aria-label="워크스페이스 역할"
          value={role}
          options={ROLES.map((value) => ({ value, label: DIRECTORY_ROLE_LABEL[value] }))}
          onValueChange={(next) => setRole(next as Exclude<DirectoryRole, "owner">)}
        />
      </DialogField>
      <DialogField label="팀">
        <FuiDropdown density="dialog" aria-label="소속 팀" value={teamId} options={teamOptions} onValueChange={setTeamId} />
      </DialogField>
      <DialogCheckbox label="게스트로 초대합니다. 초대된 플랜만 봅니다." checked={guest} onChange={setGuest} />
      {error ? <Caption1 className={styles.line}>{error}</Caption1> : null}
    </AppDialog>
  );
}

function newResource(
  displayName: string,
  email: string,
  role: Exclude<DirectoryRole, "owner">,
  kind: DirectoryKind,
  teamId: string | undefined,
): Omit<UserResource, "id"> {
  return {
    displayName,
    email: email.trim(),
    roleTitle: kind === "guest" ? "게스트" : DIRECTORY_ROLE_LABEL[role],
    teamId,
    employmentType: kind === "guest" ? "partner" : "permanent",
    grade: "mid",
    skills: [],
    isActive: true,
  };
}
